/* eslint-disable react-hooks/exhaustive-deps */
import { Divider, FormControl, Grid2, IconButton, OutlinedInput, Step, StepLabel, Stepper } from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import { ContentResponse } from '../../../../data/types/content.interface';
import { UsedIcon, UnusedIcon, NotInUseIcon, ViewIcon } from '../../../shared/components/AppIcons';
import theme from '../../../../core/theme';
import FinalFileContentDetails from '../../../content-creation/components/partB/finalFileContentDetails';
import ContentsService from '../../../content-creation/services/contents.services';
import VideoPlayer from '../../../shared/components/VideoPlayer';

const useStyles = makeStyles({
  progressBar: {
    '& .MuiSvgIcon-root': {
      color: '#4db193 !important',
    },
    '& .MuiStepLabel-label': {
      fontFamily: 'Lato !important',
    },
  },
  list: {
    margin: '11px 0px 11px 34px',
  },
  smallText: {
    fontSize: '16px',
    color: theme.palette.outlineInactive.main,
  },
  inputBox: {
    width: '250px',
    background: '#fff',
    height: '40px',
    padding: '8px',
  },
  link: {
    width: '95%',
    wordBreak: 'break-all',
  },
  downIcon: {
    '& path': {
      fill: '#000 !important',
    },
  },
  path: {
    border: '1px solid #DE5852',
    wordBreak: 'break-all',
    width: '100%',
    borderRadius: '4px',
    height: '40px',
    alignItems: 'center',
  },
  outlineInputField: {
    height: '40px',
    // Default border
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    // Border on hover
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    // Border on focus
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
      borderWidth: '1px !important',
    },
    // Remove native input outline
    '& input': {
      outline: 'none !important',
    },
  },
  overflow: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

interface Props {
  cont: ContentResponse | null;
  setRefresh: Dispatch<SetStateAction<boolean>>;
}

const steps = [
  'Final File',
  'Tag and Link MP File',
  'Marketing Platform Section',
  'Link Thumbnail',
  'Track Music License Details',
  'Enter Other Details',
  'Publish, to Marketing Analytics',
];

const FinalFileDetailsForMarketing: React.FC<Props> = ({ cont, setRefresh }) => {
  const classes = useStyles();
  const [openFinalDetailModal, setOpenFinalDetailModal] = useState(false);
  const [contentAccessLink, setContentAccessLink] = useState('');
  const [status] = useState(cont?.marketingPlatformDetails[0]?.publishedStatus);

  const fetchRealContent = async (url: string) => {
    let contentUrl = '';
    if (url) {
      if (url.includes('localhost')) {
        const accessContent = await ContentsService.fetchRealS3Content(url);
        setContentAccessLink(accessContent.url);
      } else {
        contentUrl = await ContentsService.fetchRealDropboxContent(url);
        setContentAccessLink(contentUrl);
      }
    }
  };

  const getAccessLink = (url: string) => {
    const dropboxPath = url.split('/V2 Solutions Team')[1];
    const filename = cont?.filename.split('-')[0];

    return `https://www.dropbox.com/home/V2%20Solutions%20Team%2F${
      dropboxPath.split(`/${filename}`)[0]
    }?preview=${filename}`;
  };

  useEffect(() => {
    if (cont?.videoUrl) {
      fetchRealContent(cont?.videoUrl);
    }
  }, [cont]);

  return (
    <>
      <Grid2 container>
        <Grid2 container direction="column" className={classes.progressBar}>
          <Stepper activeStep={6} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Grid2>

        <Grid2 container direction="column" width="-webkit-fill-available">
          <Grid2 container mt={4}>
            <Grid2>
              <span className={classes.smallText}>
                <VideoPlayer width="640px" videoUrl={contentAccessLink || ''} thumbnail={cont?.thumbnail}/>
              </span>
            </Grid2>

            <Grid2 style={{ cursor: 'pointer' }} container direction="column" ml={3}>
              <Grid2>
                <span>Usage</span>
              </Grid2>
              <Grid2>
                {status === 'Unpublished' ? (
                  <span>
                    <UnusedIcon viewBox="5 7 30 30" sx={{ fontSize: '42px' }} /> Unpublished
                  </span>
                ) : status === 'Scheduled' ? (
                  <span>
                    <NotInUseIcon viewBox="5 7 30 30" sx={{ fontSize: '42px' }} /> Scheduled
                  </span>
                ) : (
                  <span>
                    <UsedIcon viewBox="5 7 30 30" style={{ fontSize: '42px' }} /> Published
                  </span>
                )}
              </Grid2>
            </Grid2>
          </Grid2>

          <Grid2 container direction="column">
            <span className={classes.smallText} style={{ color: 'black' }}>
              Date Added: {moment(cont?.createdAt).format('YYYY-MM-DD')}
            </span>
            <span className={classes.smallText} style={{ color: 'black' }}>
              Last Updated on Dropbox: {moment(cont?.lastUpdatedAt).format('YYYY-MM-DD')}
            </span>
          </Grid2>

          <Grid2 container mt={2} direction="column">
            <label>Final File*</label>
            <Grid2 container width="100%">
              <Grid2 width="95%">
                <FormControl fullWidth sx={{ height: '40px' }}>
                  <OutlinedInput className={classes.outlineInputField} value={cont?.filename} />
                </FormControl>
              </Grid2>
              <Grid2 container justifyContent="flex-end" width="5%">
                <IconButton onClick={() => setOpenFinalDetailModal(true)} role="button" sx={{ padding: 0 }}>
                  <ViewIcon viewBox="0 0 42 42" />
                </IconButton>
              </Grid2>
            </Grid2>
          </Grid2>

          <Grid2 container mt={1} direction="column" width="100%">
            <Grid2>
              <label>Title*</label>
            </Grid2>
            <Grid2>
              <FormControl fullWidth sx={{ height: '40px' }}>
                <OutlinedInput className={classes.outlineInputField} value={cont?.title} />
              </FormControl>
            </Grid2>
          </Grid2>

          <Grid2 container direction="column" mt={2} width="100%">
            <label>Final File Link</label>
            <Grid2 container mt={1} className={classes.path} p={0.5}>
              {cont && (
                <a
                  href={cont.videoUrl.includes('localhost') ? contentAccessLink : getAccessLink(cont.videoUrl)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#DE5852', textDecoration: 'none' }}
                  className={classes.overflow}
                >
                  {cont?.videoUrl}
                </a>
              )}
            </Grid2>
          </Grid2>

          <Divider
            orientation="horizontal"
            flexItem
            sx={{
              backgroundColor: '#E1E3E8',
              paddingBottom: '1px',
              marginTop: '16px',
            }}
          />
        </Grid2>
      </Grid2>

      {openFinalDetailModal && cont && (
        <FinalFileContentDetails setOpenDetailsModal={setOpenFinalDetailModal} id={cont?.id} setRefresh={setRefresh} />
      )}
    </>
  );
};

export default FinalFileDetailsForMarketing;
